import type { JSONContent } from "@tiptap/core";
import {
  CONDITIONAL_INLINE_ATTR,
  CONDITIONAL_NODE_NAME,
  CONDITIONAL_TEMPLATE_ATTR,
  CONTEXT_ATTR,
  CONTEXT_NODE_NAME,
  DATA_KEY_ATTR,
  DATA_KEY_NODE_NAME,
  DID_ATTR,
  DID_NODE_NAME,
  HANDLEBARS_INLINE_ATTR,
  HANDLEBARS_NODE_NAME,
  HANDLEBARS_TEMPLATE_ATTR,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/constants";
import type { DynamicNodeContextValue } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-context";
import {
  markdownManager,
  parseMarkdownToTiptapDocument,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/render-markdown";
import { collectReferencedKeys } from "src/tiptap/components/tiptap-node/dynamic-nodes/resolution";

export type DynamicNodeExportResolver = Pick<
  DynamicNodeContextValue,
  "evaluateHandlebars" | "resolveContext" | "resolveDataKey"
>;

const TABLE_ROW_CONDITION_ATTR = "condition";
const PLACEHOLDER_START = "\uE000";
const PLACEHOLDER_END = "\uE001";
const RAW_IF_KEY_PATTERN = /\{\{[#/]?\s*(?:if|unless)?\s*([A-Za-z0-9_.-]+)\b/g;
const IGNORED_TEMPLATE_KEYS = new Set([
  "and",
  "each",
  "else",
  "eq",
  "gt",
  "gte",
  "if",
  "ifEquals",
  "lt",
  "lte",
  "ne",
  "not",
  "or",
  "unless",
  "with",
]);

const stringAttr = (node: JSONContent, attr: string) => {
  const value = node.attrs?.[attr];
  return typeof value === "string" ? value : "";
};

const containsRawIfBlock = (value: string) =>
  value.includes("{{#if") || value.includes("{{/if}}");

const rowIsVisible = (condition: string, resolver: DynamicNodeExportResolver) =>
  resolver.evaluateHandlebars(`{{#if ${condition}}}1{{/if}}`).trim() !== "";

const withoutRowCondition = (node: JSONContent): JSONContent => {
  if (node.type !== "tableRow" || !node.attrs) {
    return node;
  }

  const { [TABLE_ROW_CONDITION_ATTR]: _condition, ...attrs } = node.attrs;
  const nextNode = { ...node };

  if (Object.keys(attrs).length > 0) {
    nextNode.attrs = attrs;
  } else {
    delete nextNode.attrs;
  }
  return nextNode;
};

const prepareExportDocument = (
  document: JSONContent,
  resolver: DynamicNodeExportResolver
) => {
  const placeholders: { value: string; token: string }[] = [];
  let hasRawIfBlock = false;
  const text = (node: JSONContent, value: string): JSONContent | null => {
    if (!value) {
      return null;
    }
    if (containsRawIfBlock(value)) {
      hasRawIfBlock = true;
    }

    return {
      type: "text",
      text: value,
      ...(node.marks ? { marks: node.marks } : {}),
    };
  };
  const markdown = (node: JSONContent, value: string): JSONContent | null => {
    if (!value) {
      return null;
    }

    const token = `${PLACEHOLDER_START}${placeholders.length}${PLACEHOLDER_END}`;
    placeholders.push({ token, value });

    return text(node, token);
  };
  const walk = (node: JSONContent): JSONContent | null => {
    if (node.type === "text" && node.text && containsRawIfBlock(node.text)) {
      hasRawIfBlock = true;
    }

    const rowCondition =
      node.type === "tableRow" &&
      typeof node.attrs?.[TABLE_ROW_CONDITION_ATTR] === "string"
        ? node.attrs[TABLE_ROW_CONDITION_ATTR].trim()
        : "";
    if (rowCondition && !rowIsVisible(rowCondition, resolver)) {
      return null;
    }

    const exportNode =
      node.type === "tableRow" ? withoutRowCondition(node) : node;

    if (exportNode.type === DATA_KEY_NODE_NAME) {
      const dataKey = stringAttr(exportNode, DATA_KEY_ATTR);
      const resolution = resolver.resolveDataKey(dataKey);

      return (resolution.isUnresolved ? text : markdown)(
        exportNode,
        resolution.value ?? ""
      );
    }

    if (exportNode.type === CONTEXT_NODE_NAME) {
      const resolution = resolver.resolveContext(
        stringAttr(exportNode, CONTEXT_ATTR)
      );
      return text(exportNode, resolution.value ?? "");
    }

    if (exportNode.type === DID_NODE_NAME) {
      const resolution = resolver.resolveContext(
        stringAttr(exportNode, DID_ATTR)
      );
      return text(exportNode, resolution.value ?? "");
    }

    if (
      exportNode.type === HANDLEBARS_NODE_NAME ||
      exportNode.type === CONDITIONAL_NODE_NAME
    ) {
      const isConditional = exportNode.type === CONDITIONAL_NODE_NAME;
      const inlineAttr = isConditional
        ? CONDITIONAL_INLINE_ATTR
        : HANDLEBARS_INLINE_ATTR;
      const templateAttr = isConditional
        ? CONDITIONAL_TEMPLATE_ATTR
        : HANDLEBARS_TEMPLATE_ATTR;
      const value = resolver.evaluateHandlebars(
        stringAttr(exportNode, templateAttr)
      );

      return (exportNode.attrs?.[inlineAttr] === false ? markdown : text)(
        exportNode,
        value
      );
    }

    if (!Array.isArray(exportNode.content)) {
      return exportNode;
    }

    const conditionalOnlyListItem =
      exportNode.type === "listItem" &&
      exportNode.content.length === 1 &&
      exportNode.content[0].type === "paragraph" &&
      exportNode.content[0].content?.length === 1 &&
      exportNode.content[0].content[0].type === CONDITIONAL_NODE_NAME;
    const content = exportNode.content
      .map(walk)
      .filter((child): child is JSONContent => Boolean(child));
    if (
      conditionalOnlyListItem &&
      content[0]?.type === "paragraph" &&
      (content[0].content?.length ?? 0) === 0
    ) {
      return null;
    }
    if (
      (exportNode.type === "bulletList" || exportNode.type === "orderedList") &&
      content.length === 0
    ) {
      return null;
    }

    return {
      ...exportNode,
      content,
    };
  };

  const documentForExport = walk(document) ?? {
    type: "doc",
    content: [],
  };

  return { document: documentForExport, hasRawIfBlock, placeholders };
};

const replacePlaceholders = (
  markdown: string,
  placeholders: { value: string; token: string }[]
) =>
  placeholders.reduce(
    (nextMarkdown, placeholder) =>
      nextMarkdown.split(placeholder.token).join(placeholder.value.trim()),
    markdown
  );

const replacePlainPlaceholdersInDocument = (
  node: JSONContent,
  placeholders: { value: string; token: string }[]
): JSONContent => {
  if (node.type === "text" && node.text) {
    return {
      ...node,
      text: placeholders.reduce(
        (nextText, placeholder) =>
          nextText.split(placeholder.token).join(placeholder.value),
        node.text
      ),
    };
  }

  return Array.isArray(node.content)
    ? {
        ...node,
        content: node.content.map((child) =>
          replacePlainPlaceholdersInDocument(child, placeholders)
        ),
      }
    : node;
};

const textContent = (node: JSONContent): string =>
  node.text ?? node.content?.map(textContent).join("") ?? "";

const isPlainMarkdownValue = (value: string) => {
  try {
    const parsed = parseMarkdownToTiptapDocument(value);
    const content = parsed.content ?? [];
    const paragraph = content[0];
    const paragraphContent = paragraph?.content ?? [];
    const textNode = paragraphContent[0];

    return (
      content.length === 1 &&
      paragraph?.type === "paragraph" &&
      paragraphContent.length === 1 &&
      textNode?.type === "text" &&
      textNode.text === value &&
      !textNode.marks
    );
  } catch {
    return false;
  }
};

const materializeEmptyParagraphs = (node: JSONContent): JSONContent => {
  if (node.type === "paragraph" && (node.content?.length ?? 0) === 0) {
    return {
      ...node,
      content: [{ type: "text", text: "\u00A0" }],
    };
  }

  return Array.isArray(node.content)
    ? {
        ...node,
        content: node.content.map(materializeEmptyParagraphs),
      }
    : node;
};

const dropSyntheticTableHeaders = (node: JSONContent): JSONContent => {
  node.content?.forEach(dropSyntheticTableHeaders);
  const firstRow = node.content?.[0];
  if (
    node.type === "table" &&
    firstRow?.type === "tableRow" &&
    firstRow.content?.every(
      (cell) => cell.type === "tableHeader" && !textContent(cell).trim()
    )
  ) {
    node.content = node.content?.slice(1);
  }
  return node;
};

const resolveMarkdown = (
  {
    document,
    hasRawIfBlock,
    placeholders,
  }: ReturnType<typeof prepareExportDocument>,
  resolver: DynamicNodeExportResolver,
  serializeMarkdown?: (document: JSONContent) => string
) => {
  const markdown = replacePlaceholders(
    serializeMarkdown
      ? serializeMarkdown(document)
      : markdownManager.serialize(document),
    placeholders
  ).replace(
    /\n(\|[ \t]*)\{\{#if\s+([^}]+)\}\}[ \t]*([\s\S]*?)\|[ \t]*\n\|[ \t]*\{\{\/if\}\}[ \t]*\|/g,
    "\n{{#if $2}}$1$3|\n{{/if}}"
  );

  return hasRawIfBlock || containsRawIfBlock(markdown)
    ? resolver.evaluateHandlebars(markdown)
    : markdown;
};

const addTemplateKeys = (keys: Set<string>, template: unknown) => {
  if (typeof template !== "string") {
    return;
  }

  const addKey = (key: string | undefined) => {
    if (key && !IGNORED_TEMPLATE_KEYS.has(key)) {
      keys.add(key);
    }
  };

  collectReferencedKeys(template).forEach(addKey);
  for (const match of template.matchAll(RAW_IF_KEY_PATTERN)) {
    addKey(match[1]);
  }
};

export const collectDataKeysFromTiptapExportDocument = (
  content: JSONContent | null | undefined
): string[] => {
  if (!content) {
    return [];
  }

  const keys = new Set<string>();
  const walk = (node: JSONContent) => {
    if (node.type === DATA_KEY_NODE_NAME) {
      const key = node.attrs?.[DATA_KEY_ATTR];
      if (typeof key === "string" && key) {
        keys.add(key);
      }
    } else if (node.type === HANDLEBARS_NODE_NAME) {
      addTemplateKeys(keys, node.attrs?.[HANDLEBARS_TEMPLATE_ATTR]);
    } else if (node.type === CONDITIONAL_NODE_NAME) {
      addTemplateKeys(keys, node.attrs?.[CONDITIONAL_TEMPLATE_ATTR]);
    } else if (node.type === "tableRow") {
      const condition = node.attrs?.[TABLE_ROW_CONDITION_ATTR];
      if (typeof condition === "string" && condition) {
        addTemplateKeys(keys, `{{#if ${condition}}}{{/if}}`);
      }
    } else if (node.type === "text" && node.text?.includes("{{")) {
      addTemplateKeys(keys, node.text);
    }

    node.content?.forEach(walk);
  };

  walk(content);
  return [...keys].sort();
};

export const createResolvedTiptapExportMarkdown = (
  document: JSONContent,
  resolver: DynamicNodeExportResolver,
  serializeMarkdown?: (document: JSONContent) => string
): string => {
  const prepared = prepareExportDocument(document, resolver);
  return resolveMarkdown(prepared, resolver, serializeMarkdown);
};

export const createResolvedTiptapExportDocument = (
  document: JSONContent,
  resolver: DynamicNodeExportResolver,
  serializeMarkdown?: (document: JSONContent) => string
): JSONContent => {
  const prepared = prepareExportDocument(document, resolver);

  if (
    !prepared.hasRawIfBlock &&
    prepared.placeholders.every(({ value }) => isPlainMarkdownValue(value))
  ) {
    return materializeEmptyParagraphs(
      replacePlainPlaceholdersInDocument(
        prepared.document,
        prepared.placeholders
      )
    );
  }

  return dropSyntheticTableHeaders(
    parseMarkdownToTiptapDocument(
      resolveMarkdown(prepared, resolver, serializeMarkdown)
    )
  );
};
